import { createFileRoute } from "@tanstack/react-router";
import { defaultGroundProp, defaultSiteProps, type SiteProp } from "building-generation/elements/site/propSite";
import type { SimpleProp, SimplePropScaleNotRequired } from "building-types/props/propTypes";
import { EnvPreset } from "gfx/EnvPreset";
import { assignToSiteLayer } from "gfx/layers";
import PerspectiveControls from "gfx/PerspectiveControls";
import { envPresetSceneLights } from "gfx/sceneLights";
import type { SHLightProbeGrid } from "gfx/threejs/higharc/SHLightProbeGrid";
import type THREEViewer from "gfx/THREEViewer";
import type { THREEViewerSystem } from "gfx/THREEViewerSystem";
import TimeOfDaySelector from "lib/components/design/TimeOfDaySelector";
import useTHREEViewerSystem from "lib/components/studio/useTHREEViewerSystem";
import { loadSimpleProps, loadSiteProps } from "lib/house/propLoading";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import withAuthentication from "../../components/withAuthentication";

// add and edit props here

const props: SimplePropScaleNotRequired[] = [
  {
    type: "SIMPLE",
    gltfURL: "DV_WoodlawnProp_APP_01.glb",
    proxyGLTFURL: "DV_NeighborHouses_Proxy_01.glb",
    x: 0,
    y: 0,
    z: 0,
    rotZ: 0,
  },
];

function DebugExteriorScenePage() {
  const viewerSystem = useRef<THREEViewerSystem>(null);
  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const [envPreset, setEnvPreset] = useState<EnvPreset>(EnvPreset.MIDDAY);

  const { renderID } = useTHREEViewerSystem({ viewerSystem, viewerContainerRef, setLoading: () => {} });

  useEffect(() => {
    if (!viewerSystem.current || renderID === 0) {
      return;
    }
    const { viewer, materialFactory } = viewerSystem.current;
    if (!viewer) {
      return;
    }

    viewer.setDebugFeaturesEnabled(true);
    if (!viewer.guiEnabled) {
      viewer.toggleGui();
    }

    viewer.setCameraSettings({
      name: "foo",
      x: -22,
      y: -950,
      z: 66,
      targetX: 0,
      targetY: 0,
      targetZ: 0,
      upX: 0,
      upY: 0,
      upZ: 1,
      ortho: false,
      fov: 60,
    });
    viewer.setControls(new PerspectiveControls(viewer.camera, viewer.container, 0, 0, 0));

    const addLightProbeGrid = function (lightProbeGrid: any) {
      viewer.scene.add(lightProbeGrid);
      lightProbeGrid.probesNeedUpdate = true;
    };

    void (async () => {
      (
        await loadSiteProps(
          [...(defaultSiteProps.filter((x) => x.type === "SITE") as SiteProp[]), defaultGroundProp],
          materialFactory,
        )
      ).forEach((siteModel) => {
        if (siteModel === undefined) {
          return;
        }
        viewer.scene.add(siteModel);
        assignToSiteLayer(siteModel);
      });

      (
        await loadSimpleProps(defaultSiteProps.filter((x) => x.type === "SIMPLE") as SimpleProp[], materialFactory)
      ).forEach((siteModel) => {
        if (siteModel) {
          viewer.scene.add(siteModel);
          assignToSiteLayer(siteModel);
        }
      });

      (await loadSimpleProps(props, materialFactory, true)).forEach((prop) => {
        if (prop) {
          viewer.scene.add(prop);
          prop.traverse((propChild) => assignToSiteLayer(propChild));
        }
      });

      await renderUpdate(viewer, addLightProbeGrid);
    })();
  }, [renderID]);

  useEffect(() => {
    const ssfxRendererScene = viewerSystem.current?.viewer.scene.ssfxRendererScene;
    if (ssfxRendererScene) {
      void ssfxRendererScene.sceneLightsManager
        .renderSceneLights(envPresetSceneLights[envPreset], {})
        .then(() => viewerSystem.current?.viewer.markRendererForUpdate());
    }
  }, [renderID, envPreset]);

  return (
    <>
      <Head>
        <title>{getPageTitle("Exterior Scene")}</title>
        <meta name="robots" content="noindex" />
        <style>{`
        select {
          color: black;
        }
        `}</style>
      </Head>

      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
        ref={viewerContainerRef}
      />
      <div
        style={{
          position: "absolute",
          right: "25px",
          bottom: "25px",
        }}
      >
        <TimeOfDaySelector envPreset={envPreset} setEnvPreset={setEnvPreset} />
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/exterior-scene")({
  component: withAuthentication(DebugExteriorScenePage, { superAdmin: true }),
});

async function renderUpdate(
  viewer: THREEViewer,
  addLightProbeGrid: (lightProbeGrid: SHLightProbeGrid, skipProbesInsideModel: boolean) => void,
) {
  const services = viewer.viewerRenderer.services;
  const siteProbeGrid = await services.createLightProbeGrid(1, 1, 1, "site");
  siteProbeGrid.position.set(0, 0, 500);
  siteProbeGrid.scale.set(250, 250, 250);
  siteProbeGrid.updateMatrix();
  siteProbeGrid.updateMatrixWorld();
  assignToSiteLayer(siteProbeGrid);
  siteProbeGrid.renderLayers.enableAll();
  addLightProbeGrid(siteProbeGrid, false);
  viewer.scene.updateLightProbesList();
}
